import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'

import { isSomething, Toaster } from '@tokeet/tv3-platform'
import { MessageService } from '@tv3/store/message/message.service'
import {
  AddMessage,
  AddMessageComplete,
  DeleteMessage,
  DeleteMessageComplete,
  GetBookingMessages,
  GetBookingMessagesComplete,
  LoadLatestMessages,
  LoadLatestMessagesComplete,
  MarkAsRead,
  MarkAsReadComplete,
  MarkOneMessageAsRead,
  MarkOneMessageAsReadComplete,
  MoveMessage,
  MoveMessageComplete,
  RemoveMessageFromFeed,
  RemoveMessageFromFeedComplete,
} from '@tv3/store/message/message.actions'
import { catchError, concatMap, filter, map, switchMap, take } from 'rxjs/operators'
import { combineLatest, Observable, of } from 'rxjs'
import { ActionFailed } from '@tokeet-frontend/utility'
import { MessageRequest } from '@tv3/interfaces/requests/message.request'
import * as R from 'ramda'
import { TemplateType } from '@tokeet-frontend/templates'
import { InquiryService } from '@tv3/store/inquiry/inquiry.service'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { Message, MessageType } from '@tv3/store/message/message.model'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { selectInquiry } from '@tv3/store/inquiry/inquiry.selectors'
import { GetGuestEmailsComplete } from '@tv3/store/guest/guest.actions'
import { selectGuestById } from '@tv3/store/guest/guest.selectors'
import { Guest } from '@tv3/store/guest/guest.model'
import { GuestService } from '@tv3/store/guest/guest.service'
import { InquiryChargeService } from '@tv3/services/inquiry-charge.service'
import { selectBrandingPreference } from '@tv3/store/preferences/preferences.selectors'

@Injectable()
export class MessageEffects {
  @Effect()
  loadLatest$ = this.actions$.pipe(
    ofType(LoadLatestMessages),
    switchMap(({ messageType }) =>
      this.messageService.getLatest(messageType).pipe(
        map((messages) => LoadLatestMessagesComplete({ messages })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  addMessage$ = this.actions$.pipe(
    ofType(AddMessage),
    switchMap(({ inquiry, ...others }) =>
      combineLatest([
        this.store.pipe(select(selectGuestById, { id: inquiry.guestId })),
        this.store.pipe(select(selectBrandingPreference)),
      ]).pipe(
        take(1),
        map(([guest, branding]) => ({ guest, branding, inquiry, ...others }))
      )
    ),
    switchMap(({ inquiry, form, branding, channel, guest }) => {
      if (form.addCost) {
        return this.inquiryChargeService.getCharges(inquiry).pipe(
          map(({ charges }) => ({
            inquiry,
            form,
            branding,
            channel,
            charges,
            guest,
          }))
        )
      } else {
        return of({ inquiry, form, branding, channel, charges: null, guest })
      }
    }),
    map(({ inquiry, form, branding, channel, charges, guest }) => {
      branding = branding || <any>{}
      let request = {
        inquiry_id: inquiry.id,
        convo_id: inquiry.convoId,
        guest_id: inquiry.guestId,
        rental_id: inquiry.rentalId,
        ref_id: inquiry.refId,
        api: R.pathOr('', ['attributes', 'api'], inquiry),
        source: inquiry.inquirySource,
        message_txt: form.messageText,
        message_html: channel ? form.messageText : form.messageHtml,
        rental_name: inquiry.rental.name,
        guest_name: guest?.name || R.pathOr('', ['guestDetails', 'name'], inquiry),
        guest_email: guest?.primaryEmail || R.pathOr('', ['guestDetails', 'email'], inquiry),
        guest_arrive: inquiry.guestArrive,
        guest_depart: inquiry.guestDepart,
        cc: R.map((i) => i.email, form.cc),
        bcc: form.bcc,
        subject: form.subject || `Re: Your inquiry about ${R.pathOr('', ['rental', 'name'], inquiry)}`,
        from: inquiry.rental.email,
        attachments: form.attachments,
      } as MessageRequest

      // @todo - this shouldn't be done here but on the backend
      if (charges && form.addCost) {
        request.charge = (charges.charge || 0).toFixed(2)
        request.discounts = charges.discountSum + charges.feeSum
        request.tax_fee = charges.taxFee
        request.sum = charges.sum
        request.quote = 1
        request.template = channel ? '' : branding.quoteTemplate || '1112223336ST-2'
        request.currency = inquiry.rental.currency ? inquiry.rental.currency.code : ''
      } else {
        if (channel) {
          request.template = ''
        } else if (form.template && form.template.type === TemplateType.Contract) {
          request.template = branding.contractTemplate || 'contract-template'
          request.contractName = form.template.name
          request.contractTemplate = form.template.id
        } else {
          request.template = branding.standardTemplate || '1112223337ST-2'
        }
      }

      return { request, inquiry }
    }),
    concatMap((payload) =>
      this.messageService.create(payload.request).pipe(
        switchMap((message) => this.getGuest(message.guestId).pipe(map((guest) => ({ message, guest })))),
        map(({ message, guest }) => {
          return {
            inquiryUpdate: {
              id: message.inquiryId,
              changes: { messages: R.append(message, payload.inquiry.messages) },
            },
            guestUpdate: { id: guest.id, changes: { emailMessages: R.append(message, guest.emailMessages) } },
          }
        }),
        switchMap((update) => [
          AddMessageComplete({ update: update.inquiryUpdate }),
          GetGuestEmailsComplete({ update: update.guestUpdate }),
        ]),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  getBookingMessages$ = this.actions$.pipe(
    ofType(GetBookingMessages),
    concatMap(({ bookingId }) =>
      this.messageService.getMessagesByInquiryId(bookingId).pipe(
        map((messages) => GetBookingMessagesComplete({ update: { id: bookingId, changes: { messages } } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  markAsRead$ = this.actions$.pipe(
    ofType(MarkAsRead),
    concatMap(({ inquiry, messageIds }) =>
      this.messageService.markAsRead(messageIds).pipe(
        map(() => {
          const messages = R.map((m: Message) => {
            let message = new Message(m)
            message.read = 1
            return message
          }, inquiry.messages)
          return { id: inquiry.id, changes: { messages } }
        }),
        map((update) => MarkAsReadComplete({ update })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  markOneAsRead$ = this.actions$.pipe(
    ofType(MarkOneMessageAsRead),
    concatMap(({ messageId }) =>
      this.messageService.markAsRead([messageId]).pipe(
        map((res) => MarkOneMessageAsReadComplete({ update: { id: messageId, changes: { read: 1 } } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  removeMessage$ = this.actions$.pipe(
    ofType(DeleteMessage),
    switchMap(({ message, inquiryId, silent }) =>
      this.messageService.remove(message, silent).pipe(
        map(() => DeleteMessageComplete({ message, inquiryId })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  removeMessageFromFeed$ = this.actions$.pipe(
    ofType(RemoveMessageFromFeed),
    switchMap(({ message }) =>
      this.messageService.removeFromFeed(message).pipe(
        map(() => RemoveMessageFromFeedComplete({ message })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  moveMessage$ = this.actions$.pipe(
    ofType(MoveMessage),
    switchMap(({ fromInquiryId, toInquiryId, message }) =>
      this.messageService.move(message.id, toInquiryId).pipe(
        switchMap((r) =>
          this.store.pipe(
            select(selectInquiry, { id: fromInquiryId }),
            filter((inquiry) => isSomething(inquiry)),
            take(1),
            map((inquiry) => {
              const updated = new Inquiry(inquiry)
              updated.messages = R.filter((m: Message) => message.id !== m.id, R.defaultTo([], inquiry.messages))
              return updated
            })
          )
        ),
        map((inquiry) => ({ id: inquiry.id, changes: { messages: inquiry.messages } })),
        map((update) => MoveMessageComplete({ update })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  constructor(
    private actions$: Actions,
    private guestService: GuestService,
    private store: Store<fromRoot.State>,
    private inquiryService: InquiryService,
    private inquiryChargeService: InquiryChargeService,
    private messageService: MessageService,
    private toast: Toaster
  ) {}

  private getGuest(guestId: string): Observable<Guest> {
    return this.store.pipe(select(selectGuestById, { id: guestId }), take(1)).pipe(
      switchMap((guest) => {
        if (isSomething(guest)) {
          return of(guest)
        } else {
          return this.guestService.get(guestId)
        }
      })
    )
  }
}
